import { Check, X } from "lucide-react";
import type { BlogFrontmatter } from "./types";

/**
 * Bild-Assets (Visual-Regel 12, final):
 * - Motiv 1 (OG/Thumbnail/Hero, 2400×1260 = 1,91:1): These-Motiv
 *   „Erst Premium Profile. Dann LinkedIn Ads." mit Ads-vs-Profil-Visual.
 * - Motiv 2 (Inline-Creative, 2400×1260): Feature-Gegenüberstellung
 *   Premium Business / Premium All-in-One / Sales Navigator Core. Stand
 *   Oktober 2026 (rund 80 €, 50 $ Werbeguthaben im Monat).
 * Motiv 1 ist bewusst logo-frei (Plattformen zeigen beim Teilen bereits
 * Firmenname/-logo), Motiv 2 enthält das Prometheus-Logo — Dateien
 * unverändert lassen, nichts neu rendern. Kein neXenio-Logo verwenden.
 */
const MOTIV_1_OG = "/assets/blog/linkedin-ads-agentur-b2b/motiv-1-og.png";
const MOTIV_2_VERGLEICH =
  "/assets/blog/linkedin-ads-agentur-b2b/motiv-2-vergleich.png";

export const frontmatter: BlogFrontmatter = {
  title:
    "LinkedIn Ads Agentur gesucht? Warum die richtige Agentur Sie erst nicht auf Ads setzen lässt",
  metaTitle: "LinkedIn Ads Agentur: erst Profile, dann Budget",
  slug: "linkedin-ads-agentur-b2b",
  description:
    "LinkedIn Ads Agentur gesucht? Warum der günstigste LinkedIn-Hebel im Premium-Profil Ihres Teams steckt und wann sich echtes LinkedIn-Ads-Budget wirklich lohnt.",
  primaryKeyword: "linkedin ads agentur",
  secondaryKeywords: [
    "linkedin premium all-in-one",
    "linkedin ads",
    "b2b ads agentur",
  ],
  author: "moritz",
  datePublished: "2026-07-14",
  dateModified: "2026-10-08",
  image: MOTIV_1_OG,
  imageAlt:
    "Erst Premium Profile. Dann LinkedIn Ads. Ads-vs-Profil-Visual: Premium-Profile im Team sind günstiger, glaubwürdiger und bringen Werbebudget mit",
  heroImage: {
    src: MOTIV_1_OG,
    alt: "Erst Premium Profile. Dann LinkedIn Ads. Ads-vs-Profil-Visual: Premium-Profile im Team sind günstiger, glaubwürdiger und bringen Werbebudget mit",
    width: 2400,
    height: 1260,
  },
  internalLinks: [
    "/einstiegsangebot",
    "/linkedin-ads",
    "/case-studies",
    "/case-studies/nexenio",
    "/blog/thought-leadership-linkedin",
  ],
  draft: false,
  readingMinutes: 7,
  kurzGesagt:
    "LinkedIn Ads über den kompletten Funnel zu schalten, ist teuer und selten der richtige erste Schritt. Der günstigere Hebel: Statten Sie drei bis fünf aktive Mitarbeiter mit einem LinkedIn Premium All-in-One-Profil aus. Es kostet rund 80 € im Monat und bringt 50 US-Dollar monatliches Werbeguthaben mit, einlösbar als Boost aus dem eigenen Profil. So bauen diese Profile täglich Reichweite und die richtigen Kontakte in Ihrer Zielgruppe auf, wie Markenbotschafter auf einem Messestand. Echtes Ads-Budget über den Unternehmens-Account setzen Sie erst danach ein, gezielt auf Website-Clicks statt auf Lead-Formulare. Eine gute LinkedIn Ads Agentur baut genau diese Reihenfolge, nicht sofort die teuerste Kampagne.",
  faq: [
    {
      q: "Was kostet LinkedIn Premium All-in-One, und lohnt es sich gegenüber Ads?",
      a: "Der Plan liegt bei rund 80 € im Monat und enthält 50 US-Dollar monatliches Werbeguthaben, einlösbar als Boost aus dem eigenen Profil. Damit bringt das Profil ein Startbudget für bezahlte Reichweite mit. Das ist ein deutlich günstigerer Einstieg als ein separates Full-Funnel-Ads-Budget.",
    },
    {
      q: "Ist LinkedIn All-in-One besser als Sales Navigator?",
      a: "Für Reichweite und Kontaktaufbau ja, weil nur All-in-One ein Werbeguthaben und Auto-Invites mitbringt. Sales Navigator Core (~110 €/Monat, 50 InMails, erweiterte Suchfilter) ist stärker im reinen, strukturierten Prospecting, boostet aber keine Beiträge. Viele Teams kombinieren beides rollenabhängig.",
    },
    {
      q: "Warum auf Website-Clicks optimieren statt auf Lead-Gen-Formulare?",
      a: "Weil ein Website-Klick zeigt, dass jemand Ihr Angebot tatsächlich sehen wollte. Ein ausgefülltes Formular sagt das nicht. Wir optimieren primär auf Website-Click und -Conversion und testen Lead-Formulare nur dort quer, wo das Einsammeln von Leads ausnahmsweise der richtige Zweck ist.",
    },
    {
      q: "Was muss ich technisch aufsetzen, bevor ich Budget ausgebe?",
      a: "Deployen Sie früh den LinkedIn Insight Tag auf Ihrer Website, kombiniert mit Website-/Signal-Tracking (z. B. Leadinfo und/oder HubSpot) und sauberem Event-Tracking. Nur so verstehen Sie, was Besucher auf der Seite tun, welche positiven Signale sie auslösen und was Sie ein Ergebnis wirklich kostet.",
    },
  ],
};

export default function Article() {
  return (
    <>
      <p>
        Die meisten Anfragen zum Thema „LinkedIn Ads Agentur“ starten mit
        derselben Annahme: Man bräuchte ein großes Werbebudget und eine
        Agentur, die es über den ganzen Funnel verteilt. Genau das ist der
        teuerste Weg, auf LinkedIn zu starten, und in den meisten Fällen der
        falsche.
      </p>
      <p>
        LinkedIn ist ein Präzisions-Kanal. Aber Präzision heißt nicht
        automatisch Ads. Der Kanal belohnt zuerst etwas anderes: sichtbare,
        aktive Menschen mit gut aufgesetzten Profilen, die in der richtigen
        Zielgruppe unterwegs sind. Dieser Artikel zeigt, in welcher
        Reihenfolge Sie auf LinkedIn wirklich planbar Pipeline aufbauen und
        woran Sie eine <strong>LinkedIn Ads Agentur</strong> erkennen, die
        diese Reihenfolge beherrscht statt Ihnen sofort ein Media-Budget zu
        verkaufen.
      </p>

      <h2>
        Warum ist es ein teurer Fehler, LinkedIn Ads über den ganzen Funnel zu
        schalten?
      </h2>
      <p>
        Weil LinkedIn Ads auf jeder Funnel-Stufe zu den teuersten
        Werbeplätzen im B2B gehören. Wer Awareness, Consideration und
        Conversion komplett über bezahlte Anzeigen abdeckt, zahlt
        Premium-Preise dafür, kalte Kontakte überhaupt erst warm zu machen:
        Arbeit, die günstiger und glaubwürdiger über Menschen läuft.
      </p>
      <p>
        Das Muster kennen wir aus jedem zweiten Erstgespräch: Ein Unternehmen
        schaltet eine breite Kampagne, das Budget versickert im oberen Funnel,
        und unten kommt zu wenig an, um die Kosten zu rechtfertigen. Der
        Fehler ist nicht das Creative. Der Fehler ist, den gesamten
        Beziehungsaufbau einzukaufen, statt ihn zu bauen.
      </p>
      <p>
        Reichweite über Anzeigen ist teuer. Reichweite über die eigenen Leute
        ist es nicht, und sie wirkt stärker, weil Menschen Menschen mehr
        vertrauen als einem Unternehmens-Logo im Feed. Genau hier setzt der
        günstigere Startpunkt an.
      </p>

      <h2>
        Was ist der günstigere Startpunkt, und warum ausgerechnet
        Mitarbeiter-Profile?
      </h2>
      <p>
        Weil ein einzelnes Premium-Profil heute drei Dinge auf einmal leistet
        und ein eigenes Werbeguthaben mitbringt. Der Hebel heißt{" "}
        <strong>LinkedIn Premium All-in-One</strong>: ein Plan, den LinkedIn
        2026 für Gründer, kleine Teams und aktive Einzelprofile eingeführt
        hat.
      </p>
      <p>
        Für rund 80 € im Monat bekommt jedes Profil unter anderem ein
        zentrales Dashboard für Sichtbarkeit und Prospecting, unbegrenzte
        Suche, tägliche Vorschläge passender Ansprechpartner, 30
        InMail-Credits, Auto-Einladungen an alle, die mit Ihren Beiträgen
        interagieren, einen KI-Schreibassistenten und{" "}
        <strong>
          50 US-Dollar monatliches Werbeguthaben, um Beiträge zu boosten
        </strong>
        . Das ist der entscheidende Punkt: Ein Startbudget für bezahlte
        Reichweite ist im Profil bereits enthalten. Es lässt sich nur als Boost
        aus dem eigenen Profil einlösen, nicht im Kampagnen-Manager. Für den
        Einstieg reicht das, ein separates Mediabudget brauchen Sie dafür
        nicht.
      </p>
      <p>
        Denken Sie an diese Profile wie an einen Messestand: Ihre Leute stehen
        sichtbar am richtigen Ort, sprechen die richtigen Personen an und
        repräsentieren die Marke, nur eben täglich und skalierbar. Drei bis
        fünf aktive Profile decken so den oberen Funnel (Awareness und
        Consideration) ab, ohne dass ein separates Media-Budget nötig wird.
        Intern nennen manche das Corporate-Influencer-Team, andere das
        Markenbotschafter- oder schlicht das LinkedIn-Marketing-Team. Der Name
        ist zweitrangig, der Effekt nicht.
      </p>
      <p>
        Wen Sie dafür auswählen und wie aus einem Profil eine Fachstimme wird,
        lesen Sie in{" "}
        <a href="/de/blog/thought-leadership-linkedin/">
          Thought Leadership auf LinkedIn
        </a>
        .
      </p>

      <h2>
        LinkedIn Premium All-in-One, Sales Navigator oder Premium Business:
        was brauchen Sie wirklich?
      </h2>
      <p>
        Die kurze Antwort: Für den Reichweiten- und Kontaktaufbau ist{" "}
        <strong>All-in-One</strong> der wirtschaftlichste Plan, weil nur er
        ein Werbeguthaben mitbringt. Sales Navigator ist stärker im reinen
        Prospecting (mehr InMails, erweiterte Suchfilter), kostet aber mehr
        und boostet keine Beiträge. Premium Business ist der günstige
        Basis-Plan ohne Werbe- und Auto-Invite-Funktionen.
      </p>

      <figure className="not-prose my-10">
        <img
          src={MOTIV_2_VERGLEICH}
          alt="Vergleich: Premium Business, Premium All-in-One und Sales Navigator Core. Nur All-in-One bringt 50 $ Werbeguthaben pro Monat mit"
          width={2400}
          height={1260}
          loading="lazy"
          className="w-full rounded-2xl"
        />
      </figure>

      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Premium Business</th>
              <th>
                <strong>Premium All-in-One</strong>
              </th>
              <th>Sales Navigator Core</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Preis (ca./Monat)</td>
              <td>~55 €</td>
              <td>
                <strong>~80 €</strong>
              </td>
              <td>~110 €</td>
            </tr>
            <tr>
              <td>Werbeguthaben inklusive</td>
              <td>
                <X aria-label="nein" className="h-4 w-4 shrink-0 text-muted-foreground" />
              </td>
              <td>
                <strong className="inline-flex items-center gap-1.5">
                  <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
                  50 $/Mo
                </strong>
              </td>
              <td>
                <X aria-label="nein" className="h-4 w-4 shrink-0 text-muted-foreground" />
              </td>
            </tr>
            <tr>
              <td>InMails pro Monat</td>
              <td>15</td>
              <td>
                <strong>30</strong>
              </td>
              <td>50</td>
            </tr>
            <tr>
              <td>Kontaktanfragen &amp; Netzwerk</td>
              <td>
                <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
              </td>
              <td>
                <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
              </td>
              <td>
                <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
              </td>
            </tr>
            <tr>
              <td>Wer hat mein Profil besucht</td>
              <td>
                <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
              </td>
              <td>
                <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
              </td>
              <td>
                <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
              </td>
            </tr>
            <tr>
              <td>Erweiterte Suchfilter</td>
              <td>
                <X aria-label="nein" className="h-4 w-4 shrink-0 text-muted-foreground" />
              </td>
              <td>
                <X aria-label="nein" className="h-4 w-4 shrink-0 text-muted-foreground" />
              </td>
              <td>
                <Check aria-label="ja" className="h-4 w-4 shrink-0 text-secondary" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        <em>
          Preise ca., LinkedIn-Listenpreis 2026, gerundet. Stand Oktober 2026.
          Nur All-in-One
          bringt Werbeguthaben mit. Verfügbarkeit von All-in-One ist regional
          noch nicht überall sofort gegeben. Ein Grund, das früh mit einer
          Agentur aufzusetzen.
        </em>
      </p>
      <p>
        Für die meisten DACH-B2B-Teams ist die Kombination eindeutig:
        All-in-One für die aktiven Profile, die posten und netzwerken, und
        Sales Navigator gezielt dort, wo jemand primär strukturiertes
        Prospecting mit erweiterten Filtern betreibt.
      </p>

      <h2>
        Wie entsteht aus diesen Profilen eine planbare Reichweiten-Maschine?
      </h2>
      <p>
        Durch eine Doppelwirkung, die parallel läuft. Jedes gut aufgesetzte
        All-in-One-Profil arbeitet an zwei Fronten gleichzeitig:
      </p>
      <p>
        <strong>Erstens Sichtbarkeit.</strong> Ein bis mehrere Beiträge pro
        Monat werden mit dem inkludierten Werbeguthaben (50 US-Dollar im
        Monat) gezielt auf
        eine hinterlegte Zielgruppe geboostet. So erscheinen Ihre Leute genau
        im Feed der richtigen Unternehmen und Personas: persönlich, nicht als
        Anzeige eines Logos.
      </p>
      <p>
        <strong>Zweitens Kontaktaufbau.</strong> Über dasselbe Profil laufen
        täglich gezielte Kontaktanfragen an die passenden Personas:
        Geschäftsführer, Vertriebsleiter, Fachexperten. Wer mit den Beiträgen
        interagiert, wird über die Auto-Invite-Funktion automatisch
        eingeladen. So wächst mit jeder Woche ein Netzwerk aus genau den
        Menschen, die kaufen könnten.
      </p>
      <p>
        Der Multiplikator entsteht, wenn beides zusammenläuft: Dieselben
        Unternehmen, die Ihre Profile über Monate aufwärmen, werden später zur
        Zielliste für das bezahlte Ads-Budget. Ein optimiertes Profil
        verbessert dabei jede einzelne Conversion, vom Profilbesuch über den
        Website-Klick bis zum Verständnis, welches Unternehmen dahintersteht,
        und zum direkten Kontaktpunkt mit der richtigen Person. Mit drei bis
        fünf solcher Profile decken Sie Awareness und Consideration ab, bevor
        Sie überhaupt einen Euro zusätzliches Media-Budget in die Hand nehmen.
      </p>

      <h2>
        Ab wann lohnt sich dann das echte LinkedIn-Ads-Budget, und worauf
        optimieren Sie?
      </h2>
      <p>
        Erst wenn die Profil-Maschine läuft und ausgeschöpft ist. Dann, und
        erst dann, setzen wir das eigentliche LinkedIn-Ads-Budget über den
        Unternehmens-Account auf, und zwar nicht am Anfang des Funnels,
        sondern im tieferen Bereich: von Consideration in Richtung Conversion.
      </p>
      <p>
        Entscheidend ist die Kennzahl, auf die diese Kampagnen optimieren:{" "}
        <strong>
          Website-Clicks und Website-Conversions, nicht Lead-Gen-Formulare.
        </strong>{" "}
        Lead-Formulare sammeln bequem Kontakte, aber oft ohne echten Bedarf
        und ohne dass die Person je Ihre Website und Ihr Angebot wirklich
        gesehen hat. Wir arbeiten primär auf den Website-Klick und die
        Website-Conversion; Lead-Formulare testen wir nur quer, in Kampagnen,
        in denen das Einsammeln von Leads ausnahmsweise der richtige Zweck
        ist.
      </p>
      <p>
        Diese Ads laufen dann sehr zielgerichtet auf konkrete Angebote
        (Webinare, Content-Assets, Case Studies) und immer gegen die
        Unternehmen, die Ihre Mitarbeiter-Profile längst aufgewärmt haben. So
        holen Sie das Maximum aus jedem Euro: Der bezahlte Klick trifft
        niemanden kalt, sondern jemanden, der Ihre Marke im Feed schon
        mehrfach gesehen hat.
      </p>

      <h2>Woran erkennen Sie eine LinkedIn Ads Agentur, die so denkt?</h2>
      <p>
        An dem, worüber sie im Erstgespräch zuerst spricht. Eine Agentur, die
        auf Pipeline optimiert, redet über Ihre Profile, Ihre Zielaccounts und
        Ihr Tracking, bevor sie über Media-Budget redet. Eine Agentur, die
        Ihnen sofort einen großen Full-Funnel-Ads-Plan verkaufen will,
        optimiert den eigenen Umsatz, nicht Ihren.
      </p>
      <p>
        Dass die Reihenfolge „erst definierte Accounts, dann Skalierung“
        trägt, ist keine Theorie: In unserem{" "}
        <a href="/de/case-studies/nexenio/">neXenio-Case</a> entstand aus einer klar
        begrenzten Zielmenge eine achtstellige Pipeline. Nicht die Marktgröße
        hat entschieden, sondern die Präzision. Und die beginnt bei Menschen
        und Profilen, nicht beim Anzeigen-Budget.
      </p>

      <h2>Fazit</h2>
      <p>
        <strong>LinkedIn Ads</strong> sind wirkungsvoll, aber sie sind der
        zweite Schritt, nicht der erste. Der wirtschaftlichste Start heißt:
        drei bis fünf aktive Mitarbeiter mit einem LinkedIn Premium
        All-in-One-Profil ausstatten, das inkludierte Werbeguthaben und den
        täglichen Kontaktaufbau nutzen, Awareness und Consideration über
        Menschen abdecken und das bezahlte Ads-Budget erst danach im
        tieferen Funnel auf Website-Conversions ansetzen. Eine LinkedIn Ads
        Agentur, die diese Reihenfolge baut statt sofort die teuerste
        Kampagne zu verkaufen, macht aus LinkedIn einen planbaren
        Umsatzkanal. Reichweite über Anzeigen ist teuer. Reichweite über Ihr
        Team ist es nicht.
      </p>

      <p>
        <strong>Der richtige erste Schritt statt des teuersten:</strong> In
        unserem <a href="/de/einstiegsangebot/">Einstiegsangebot</a> setzen wir
        Ihre LinkedIn-Lizenzen optimal auf, klären, welche Rollen Ihr Team auf
        LinkedIn wirklich braucht, und liefern die ersten{" "}
        <strong>100 passenden Follower kostenlos</strong> mit. Danach sind Sie
        richtig aufgestellt, um überhaupt sinnvoll in LinkedIn Ads zu
        investieren. Mehr zu unserem Vorgehen:{" "}
        <a href="/de/linkedin-ads/">LinkedIn Ads mit Prometheus</a> oder
        Termin direkt im <a href="/de/booking/">Strategiegespräch</a>.
      </p>
      <p>
        <strong>PS:</strong> Bauen Sie das Tracking schon während des Aufbaus
        auf, nicht erst danach. LinkedIn Insight Tag, Website-/Signal-Tracking
        (Leadinfo/HubSpot) und Event-Tracking gehören live, bevor das erste
        Budget fließt. Sonst verbrennen Sie die Lernkurve.
      </p>
    </>
  );
}
